import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTierAnalytics, useCostAnalysis } from '@/hooks/useAnalytics';
import { TierBadge } from '@/components/dashboard/TierBadge';
import { formatLatency, formatCost, getTierColor } from '@/lib/utils';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import api from '@/lib/api';
import { Clock, TrendingUp, Layers, DollarSign } from 'lucide-react';

export function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState('24');
  const { analytics } = useTierAnalytics(parseInt(timeRange));
  const { analysis: costAnalysis } = useCostAnalysis(7);
  const [latencyData, setLatencyData] = useState([]);

  useEffect(() => {
    const fetchLatency = async () => {
      try {
        const response = await api.getLatencyDistribution(parseInt(timeRange));
        setLatencyData(response.data.data || []);
      } catch (err) {
        console.error('Failed to fetch latency data:', err);
      }
    };
    fetchLatency();
  }, [timeRange]);

  // Prepare intent distribution data
  const intentData = analytics?.intent_distribution
    ? Object.entries(analytics.intent_distribution).map(([intent, count]) => ({
        name: intent,
        value: count,
      }))
    : [];

  // Prepare tier confidence data
  const tierConfidenceData = analytics?.tier_stats
    ? Object.entries(analytics.tier_stats).map(([tier, stats]) => ({
        tier,
        name: tier.replace('tier', 'T').replace('_local_fast', '1').replace('_local_smart', '2').replace('_cloud', '3'),
        count: stats.count,
        avgConfidence: (stats.avg_confidence * 100).toFixed(1),
        color: getTierColor(tier),
      }))
    : [];

  const COLORS = ['#10B981', '#F59E0B', '#8B5CF6', '#3B82F6', '#EF4444', '#EC4899'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-lg">
          <p className="font-medium text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('latency') ? formatLatency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">
            ANALYTICS
          </h1>
          <p className="text-muted-foreground mt-1">
            Deep dive into routing patterns and performance
          </p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-40" data-testid="time-range-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 1 hour</SelectItem>
            <SelectItem value="6">Last 6 hours</SelectItem>
            <SelectItem value="24">Last 24 hours</SelectItem>
            <SelectItem value="72">Last 3 days</SelectItem>
            <SelectItem value="168">Last 7 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <Layers className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{analytics?.total_decisions || 0}</p>
                <p className="text-xs text-muted-foreground">Total Decisions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {tierConfidenceData.map((tier) => (
          <Card key={tier.tier} className="border-border bg-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <TierBadge tier={tier.tier} size="sm" />
                  <p className="text-2xl font-bold mt-2">{tier.count}</p>
                  <p className="text-xs text-muted-foreground">Requests</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-mono" style={{ color: tier.color }}>
                    {tier.avgConfidence}%
                  </p>
                  <p className="text-[10px] text-muted-foreground">Avg Confidence</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Latency Over Time */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Latency Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={latencyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 10, fill: '#71717a' }}
                    tickFormatter={(value) => value.split('T')[1] || value}
                  />
                  <YAxis 
                    tick={{ fontSize: 10, fill: '#71717a' }}
                    tickFormatter={(value) => `${value}ms`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="avg_latency" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    dot={false}
                    name="Avg latency"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="min_latency" 
                    stroke="#10B981" 
                    strokeWidth={1}
                    dot={false}
                    name="Min latency"
                    strokeDasharray="5 5"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="max_latency" 
                    stroke="#EF4444" 
                    strokeWidth={1}
                    dot={false}
                    name="Max latency"
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Intent Distribution */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Intent Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {intentData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={intentData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {intentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No intent data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Analysis */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Cost Analysis (7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Daily Cost Chart */}
            <div className="lg:col-span-2 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costAnalysis?.daily_costs || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10, fill: '#71717a' }}
                    tickFormatter={(value) => value.split('-').slice(1).join('/')}
                  />
                  <YAxis 
                    tick={{ fontSize: 10, fill: '#71717a' }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCost(value), 'Cost']}
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '6px',
                    }}
                  />
                  <Bar dataKey="cost" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Cost Summary */}
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-secondary/50 border border-border">
                <p className="text-xs text-muted-foreground mb-1">Actual Cost</p>
                <p className="text-2xl font-bold">{formatCost(costAnalysis?.total_actual_cost || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
                <p className="text-xs text-violet-400 mb-1">If All Cloud</p>
                <p className="text-2xl font-bold text-violet-400">
                  {formatCost(costAnalysis?.hypothetical_cloud_cost || 0)}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs text-emerald-400 mb-1">Total Savings</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {formatCost(costAnalysis?.savings || 0)}
                </p>
                <p className="text-xs text-emerald-400 mt-1">
                  {costAnalysis?.savings_percentage || 0}% reduction
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AnalyticsPage;
